import { useState } from 'react'
import { Check, X } from 'lucide-react'
import { toast } from 'sonner'
import z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Button } from '@/app/components/ui/button'
import { Input } from '@/app/components/ui/input'
import { FormError } from '@/app/components/ui/FormError'
import { DiscreteLoader } from '@/app/components/ui/discrete-loader'
import { posCustomerService, CreateCustomerPayload } from '@/app/services/pos.service'

const formSchema = z.object({
  name: z.string().min(1, 'El nombre es obligatorio').max(255, 'Demasiado largo'),
  phone: z.string().min(10, 'El teléfono debe tener al menos 10 dígitos').max(30, 'Demasiado largo'),
  email: z.union([z.string().email('Formato de correo inválido'), z.literal('')]).optional(),
  notes: z.string().optional(),
  is_active: z.boolean().default(true),
})

type FormValidate = z.infer<typeof formSchema>

export interface NewCustomerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  setShowNewCustomerModal: (show: boolean) => void
  onSuccess?: () => void
}

export function NewCustomerModal({
  open,
  onOpenChange,
  setShowNewCustomerModal,
  onSuccess
}: NewCustomerModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValidate>({
    resolver: zodResolver(formSchema as any),
    mode: 'onChange',
    defaultValues: { name: '', phone: '', email: '', notes: '', is_active: true },
  })

  const onSubmit = async (formData: FormValidate) => {
    try {
      // Si el email esta vacio, lo mandamos como undefined para que el backend lo acepte como null
      const payload: CreateCustomerPayload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email === '' ? undefined : formData.email,
        notes: formData.notes,
        is_active: formData.is_active,
      } as CreateCustomerPayload

      await posCustomerService.createCustomer(payload)
      toast.success('Cliente registrado exitosamente')
      setShowNewCustomerModal(false)
      reset()

      if (onSuccess) {
        onSuccess()
      }
    } catch (_error) {
      toast.error('Error al registrar el cliente')
    }
  }

  // Se ejecuta cuando se cierra el modal para limpiar el form
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      reset()
    }
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Registro Rápido de Cliente</DialogTitle>
          <DialogDescription className="sr-only">
            Formulario rápido para registrar un nuevo cliente desde el punto de venta
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit as any)} className="space-y-4">
          <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
            <p className="text-sm text-primary/90">
              Registra un nuevo cliente para poder asignarle puntos y llevar un historial de compras.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Nombre Completo <span className="text-red-600">*</span>
            </label>
            <Input {...register('name')} placeholder="Ej: Juan Pérez García" />
            {errors.name && <FormError message={errors.name.message} />}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Teléfono <span className="text-red-600">*</span>
              </label>
              <Input {...register('phone')} placeholder="555-1234567" />
              {errors.phone && <FormError message={errors.phone.message} />}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Email
              </label>
              <Input {...register('email')} type="email" placeholder="correo@ejemplo.com" />
              {errors.email && <FormError message={errors.email.message} />}
            </div>
          </div>

          {/* 
          // Descomentar despues de cambios de backend
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="mb-2 text-xs text-slate-600">
              Dirección (Opcional)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Calle" className="h-8 text-sm" />
              <Input placeholder="Número" className="h-8 text-sm" />
              <Input placeholder="Colonia" className="h-8 text-sm" />
              <Input placeholder="Código Postal" className="h-8 text-sm" />
            </div>
          </div>
          */}

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Comentarios
            </label>
            <Input {...register('notes')} placeholder="Notas adicionales..." />
            {errors.notes && <FormError message={errors.notes.message} />}
          </div>

          <div className="flex gap-3 border-t border-slate-200 pt-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <DiscreteLoader />
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Guardar y Seleccionar
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={() => {
                setShowNewCustomerModal(false)
                reset()
              }}
              className="flex-1"
            >
              <X className="mr-2 h-4 w-4" />
              Cancelar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
